// src/pages/WastagePage.js
import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Button, Typography, Paper, Grid, TextField,
    CircularProgress, Alert, Autocomplete, Chip,
    Select, MenuItem, InputLabel, FormControl // <-- AÑADIR ESTOS
} from '@mui/material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid } from '@mui/x-data-grid';

import api from '../api/index.js';
import AuthContext from '../context/AuthContext.js';



const WastagePage = () => {
    const { user } = useContext(AuthContext);

    // --- ESTADOS ---
    // Selección de tienda
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(user?.stores?.[0] || '');
    
    // Formulario
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [reason, setReason] = useState('');
    
    // Datos y UI
    const [inventory, setInventory] = useState([]);
    const [wastageHistory, setWastageHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // --- CARGA DE DATOS ---
    useEffect(() => {
        // Cargar la lista de todas las tiendas disponibles para el selector
        const fetchStores = async () => {
            try {
                const response = await api.get('/stores');
                setStores(response.data);
            } catch (err) {
                console.error("Error al cargar tiendas:", err);
            }
        };
        fetchStores();
    }, []);

    // Cargar inventario e historial cuando se selecciona una tienda
    useEffect(() => {
        const fetchDataForStore = async () => {
            if (!selectedStore) {
                setInventory([]);
                setWastageHistory([]);
                return;
            }
            setLoading(true);
            setError('');
            try {
                const [inventoryRes, historyRes] = await Promise.all([
                    api.get(`/inventory/store/${selectedStore}`),
                    api.get(`/wastage?store_id=${selectedStore}`) // Usamos el nuevo filtro
                ]);
                setInventory(inventoryRes.data);
                setWastageHistory(historyRes.data);
            } catch (err) {
                setError('No se pudieron cargar los datos de la tienda seleccionada.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchDataForStore();
    }, [selectedStore]);

    // --- MANEJADORES ---
    const handleRegisterWastage = async () => {
        if (!selectedProduct || quantity <= 0) {
            setError('Por favor, selecciona un producto y una cantidad válida.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const payload = {
                store_id: selectedStore, // <-- Usamos la tienda seleccionada
                product_id: selectedProduct.product_id,
                quantity: parseInt(quantity, 10),
                reason: reason
            };
            await api.post('/wastage', payload);
            alert('Merma registrada exitosamente.');
            setSelectedProduct(null);
            setQuantity(1);
            setReason('');
            // Recargar datos para la tienda actual
            const event = { target: { value: selectedStore } };
            const fetchDataForStore = async () => {
                if (!selectedStore) {
                    setInventory([]);
                    setWastageHistory([]);
                    return;
                }
                setLoading(true);
                setError('');
                try {
                    const [inventoryRes, historyRes] = await Promise.all([
                        api.get(`/inventory/store/${selectedStore}`),
                        api.get(`/wastage?store_id=${selectedStore}`) // Usamos el nuevo filtro
                    ]);
                    setInventory(inventoryRes.data);
                    setWastageHistory(historyRes.data);
                } catch (err) {
                    setError('No se pudieron cargar los datos de la tienda seleccionada.');
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            };
            fetchDataForStore();

        } catch (err) {
            setError(err.response?.data?.message || 'Error al registrar la merma.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleMarkAsReplaced = async (id) => {
        setLoading(true);
        try {
            await api.put(`/wastage/${id}/replace`);
            alert('Merma marcada como reemplazada.');
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al procesar la reposición.');
        } finally {
            setLoading(false);
        }
    }

    // --- COLUMNAS DE LA TABLA ---
    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'product_name', headerName: 'Producto', width: 250 },
        { field: 'quantity', headerName: 'Cantidad', width: 100 },
        { field: 'reason', headerName: 'Razón', width: 250 },
        { field: 'user_name', headerName: 'Registrado por', width: 180 },
        { 
            field: 'status', headerName: 'Estado', width: 120,
            renderCell: (p) => <Chip label={p.value} color={p.value === 'Lost' ? 'error' : 'success'}/>
        },
        { 
            field: 'wastage_date', headerName: 'Fecha', width: 180,
            type: 'dateTime', valueGetter: (p) => new Date(p.value)
        },
        {
            field: 'actions', headerName: 'Acciones', width: 150,
            renderCell: (params) => (
                params.row.status === 'Lost' && (
                    <Button
                        variant="outlined" size="small"
                        onClick={() => handleMarkAsReplaced(params.row.id)}
                    >
                        Reemplazado
                    </Button>
                )
            )
        }
    ];

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Gestión de Mermas</Typography>
            
 <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Seleccionar Tienda</InputLabel>
                <Select
                    value={selectedStore}
                    label="Seleccionar Tienda"
                    onChange={(e) => setSelectedStore(e.target.value)}
                >
                    {stores.map((store) => (
                        <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {selectedStore && (
                <>

            {/* --- FORMULARIO DE REGISTRO --- */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Registrar Nueva Merma</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <Autocomplete
                            options={inventory}
                            getOptionLabel={(option) => `${option.name} (Stock: ${option.quantity})`}
                            value={selectedProduct}
                            onChange={(e, newValue) => setSelectedProduct(newValue)}
                            renderInput={(params) => <TextField {...params} label="Buscar Producto en Inventario" />}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth label="Cantidad" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth label="Razón (Opcional)" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            fullWidth variant="contained" color="error"
                            onClick={handleRegisterWastage} disabled={loading}
                            sx={{ height: '56px' }}
                        >
                            Registrar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>


             {/* Historial de Mermas */}
                    <Typography variant="h6" gutterBottom>Historial de Mermas de la Tienda</Typography>
                    <Box sx={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={wastageHistory}
                            columns={columns}
                            loading={loading}
                            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default WastagePage;